import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsClient, CLIENT_STAGES } from './api';
import { Modal, Field, inputCls, btnPrimary, StatusBadge } from './shared';
import { timeAgo } from './api';

const AtsClients: React.FC = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState<AtsClient[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', domain: '', location: '', stage: 'prospect', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    atsFetch(token, `/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`)
      .then((d) => setClients(d.clients))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, search]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load, search]);

  const create = async () => {
    if (!token || !form.name.trim()) return;
    setSaving(true);
    try {
      await atsFetch(token, '/clients', { method: 'POST', body: JSON.stringify(form) });
      setShowCreate(false);
      setForm({ name: '', domain: '', location: '', stage: 'prospect', description: '' });
      load();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const allSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));
  const toggle = (id: number, on: boolean) => {
    setSelected((prev) => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });
  };

  const bulkDelete = async () => {
    if (!token || !selected.size || deleting) return;
    const n = selected.size;
    if (!window.confirm(
      `Delete ${n} client${n === 1 ? '' : 's'}?\n\nThis permanently deletes their roles and pipelines too (contacts stay as people; placement history is kept).\n\nThis cannot be undone.`
    )) return;
    setDeleting(true);
    try {
      await atsFetch(token, '/clients/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }) });
      setSelected(new Set());
      load();
    } catch (e: any) { setError(e.message); }
    setDeleting(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className={`${inputCls} max-w-xs`} />
        {selected.size > 0 && (
          <span className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <button onClick={bulkDelete} disabled={deleting} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </span>
        )}
        <button onClick={() => setShowCreate(true)} className={btnPrimary}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New client
        </button>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 mb-1">No clients yet.</p>
          <p className="text-xs text-gray-400">A client is a business you recruit for — add your first and open a role under it.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="pl-4 pr-1 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => setSelected(e.target.checked ? new Set(clients.map((c) => c.id)) : new Set())}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-2 py-2.5 font-medium">Client</th>
                <th className="px-3 py-2.5 font-medium">Stage</th>
                <th className="px-3 py-2.5 font-medium text-center">Open roles</th>
                <th className="px-3 py-2.5 font-medium text-center">Contacts</th>
                <th className="px-3 py-2.5 font-medium text-center">Placements</th>
                <th className="px-3 py-2.5 font-medium">Location</th>
                <th className="px-5 py-2.5 font-medium text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className={`border-b border-gray-50 last:border-0 hover:bg-gray-50/60 ${selected.has(c.id) ? 'bg-red-50/40' : ''}`}>
                  <td className="pl-4 pr-1 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={(e) => toggle(c.id, e.target.checked)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-2 py-3">
                    <Link to={`/ats/clients/${c.id}`} className="font-medium text-gray-900 hover:text-blue-700">{c.name}</Link>
                    {c.domain && <span className="ml-2 text-xs text-gray-400">{c.domain}</span>}
                  </td>
                  <td className="px-3 py-3"><StatusBadge value={c.stage} /></td>
                  <td className="px-3 py-3 text-center text-gray-700">{c.open_roles || 0}</td>
                  <td className="px-3 py-3 text-center text-gray-700">{c.contact_count || 0}</td>
                  <td className="px-3 py-3 text-center text-gray-700">{c.placement_count || 0}</td>
                  <td className="px-3 py-3 text-gray-500">{c.location || '—'}</td>
                  <td className="px-5 py-3 text-right text-xs text-gray-400">{timeAgo(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal title="New client" onClose={() => setShowCreate(false)}>
          <div className="space-y-3">
            <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} autoFocus /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Domain"><input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="acme.com" className={inputCls} /></Field>
              <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Stage">
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={inputCls}>
                {CLIENT_STAGES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Notes"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls} /></Field>
            <div className="flex justify-end pt-1">
              <button onClick={create} disabled={saving || !form.name.trim()} className={btnPrimary}>{saving ? 'Creating…' : 'Create client'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AtsClients;
